import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Skeleton — Fastn Design System contract.
 *
 * A 150ms appearance delay stops a fast response from flashing a placeholder,
 * the shimmer runs at 1.2s, and the container reports `aria-busy` so assistive
 * tech announces the wait. Under `prefers-reduced-motion` the shimmer is static.
 */
export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Skip the 150ms delay when the wait is already known to be long. */
  immediate?: boolean;
}

function Skeleton({ className, immediate = false, ...props }: SkeletonProps) {
  const [shown, setShown] = React.useState(immediate);

  React.useEffect(() => {
    if (immediate) return;
    const t = setTimeout(() => setShown(true), 150);
    return () => clearTimeout(t);
  }, [immediate]);

  return (
    <div
      aria-busy="true"
      className={cn(
        "rounded-md bg-raised motion-safe:animate-[fs-skeleton_1.2s_ease-in-out_infinite]",
        shown ? "opacity-100" : "opacity-0",
        className,
      )}
      {...props}
    />
  );
}

export { Skeleton };
